import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Shield, X, Eye, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Global banner shown when an admin is impersonating a tenant.
 * This should be rendered at the top of the app layout.
 */
export const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const { isImpersonating, impersonationData, exitImpersonation } = useImpersonation();
  const [exiting, setExiting] = useState(false);

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitImpersonation();
      // Navigate back to admin tenants page
      navigate('/admin/tenants');
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
      setExiting(false);
    }
  };

  if (!isImpersonating || !impersonationData) {
    return null;
  }

  const isReadOnly = impersonationData.read_only;

  return (
    <div 
      className={`sticky top-0 z-[100] px-4 py-2 flex items-center justify-between gap-4 shadow-lg ${
        isReadOnly 
          ? 'bg-amber-500 text-amber-950' 
          : 'bg-purple-600 text-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          <span className="font-bold text-sm uppercase tracking-wide">
            Impersonating
          </span>
        </div>
        
        <div className="h-5 w-px bg-current opacity-30" />
        
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4" />
          <span className="font-medium">
            {impersonationData.tenant_name}
          </span>
        </div>
        
        {isReadOnly && (
          <>
            <div className="h-5 w-px bg-current opacity-30" />
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-600/30 rounded text-amber-950">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="font-semibold text-xs uppercase">Read-Only</span>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs opacity-75">
          Expires: {new Date(impersonationData.expires_at).toLocaleTimeString()}
        </span>
        
        <Button
          onClick={handleExit}
          disabled={exiting}
          size="sm"
          variant="secondary"
          className={`font-medium ${
            isReadOnly 
              ? 'bg-amber-700 hover:bg-amber-800 text-white border-amber-600' 
              : 'bg-purple-700 hover:bg-purple-800 text-white border-purple-500'
          }`}
        >
          {exiting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Exiting...
            </>
          ) : (
            <>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Exit Impersonation
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
